/**
 * PSTUDIO-4083 — the two INPUTS every node-numbering entrance hands to
 * `assignNodeNumbers`, in one place.
 *
 * They were hand-rolled twice: once as `tableStore.getAppendNumberingAnchor` /
 * `getExistingNodeNumbers`, and once inline inside `createTaskNode`. The two
 * copies then drifted in the way that matters — the store's pair learned to skip
 * the rows inside the soft-delete window and the task-node copy did not, so a
 * task created right after a delete could be handed a number a still-present row
 * held (PSTUDIO-3990's window). Keeping the arithmetic here makes the only
 * remaining difference between callers the one that is meant to differ: WHICH
 * rows they pass (`tableStore.rowsForNumbering` filters the window; a caller
 * holding a plain array is responsible for its own).
 *
 * Pure and store-free on purpose, so the node-env suite can cover both without
 * the heavy store.
 */

import {
  collectReferencedNodeNumbers,
  toValidNodeNumber,
} from './paste-node-numbering';
import { isGroupRow } from './utils/group-row-guards';

/** A row as numbering sees it: its own number, a `nodeType` only `isGroupRow`
 *  interprets, and the three fields that can POINT AT another node's number.
 *  Structural on purpose — naming the store's row type here would make this
 *  module (and every caller's test) depend on the store's whole shape. */
type NumberedRow = {
  nodeNumber?: unknown;
  nodeType?: unknown;
  nextNodes?: unknown;
  whatsNext?: unknown;
  richAssetContent?: unknown;
};

/** `isGroupRow` is deliberately case-insensitive (a legacy row carries `'G'`);
 *  the cast only re-states the two fields it actually reads. */
const isGroupHeader = (row: NumberedRow): boolean =>
  isGroupRow(row as Parameters<typeof isGroupRow>[0]);

/**
 * PSTUDIO-3992: the anchor an APPEND numbers from — the LAST row carrying a
 * usable number, group headers excluded (a header is a separator, never a
 * numbering reference). Feeding this to `assignNodeNumbers` as `reference` with
 * `upper = null` is what makes "add at the end" produce a number next to the
 * end, instead of `getMaxNodeNumber() + 1` — which jumps to whatever the largest
 * number in the solution happens to be, however far away.
 */
export function appendNumberingAnchorOf(
  rows: readonly NumberedRow[]
): number | null {
  return rows.reduce<number | null>(
    (ref, row) =>
      isGroupHeader(row) ? ref : (toValidNodeNumber(row.nodeNumber) ?? ref),
    null
  );
}

/**
 * PSTUDIO-2871 / PSTUDIO-4083: the numbers a numbering pass must know about,
 * split into the two kinds the allocator treats differently — in ONE walk.
 *
 * - `own` — numbers rows actually HOLD. Group headers are deliberately NOT
 *   skipped (unlike the anchor above): a header should carry no number, but a
 *   legacy one that does still OCCUPIES it, and a number something holds is
 *   taken no matter who holds it. This is the collision set, not the "which rows
 *   get numbered" set — that exclusion lives at the assignment.
 * - `referenced` — numbers a row merely POINTS AT. Deleting a node does not
 *   rewrite the rows that referenced it, so after node 4 is deleted the number 4
 *   is still in the data, and handing it to a new node makes that node silently
 *   inherit every edge aimed at the one that died. That turns an error the user
 *   can SEE (the dangling reference Edan asked for on 2026-09-08) into a graph
 *   that is quietly wrong. `collectReferencedNodeNumbers` reads the same three
 *   fields, through the same patterns, that the paste-time rewriter writes.
 *
 * Why they must not be ONE set: both block a collision, but only `own` may
 * anchor Fallback's `max + 1` — see `assignNodeNumbers`, which takes them as
 * separate arguments for exactly this reason.
 *
 * COST — measured, because the honest number is nothing like the shape of the
 * loop. Reading a row's own number is one field read; collecting what it points
 * at walks `nextNodes`/`whatsNext` and runs two regexes over the whole of
 * `richAssetContent`, so this is O(rows x asset size), not O(rows), and on a
 * MobX observable array every step of that walk goes through a proxy. On 5,000
 * rows: 0.19 ms before this ticket, 16.5 ms with a 4.5 KB carousel on a fifth of
 * the rows, 37 ms with a 2 KB asset on every row, and ~1.1 s with 40 options on
 * every row. Call it ONCE per gesture and reuse the result — `addEmptyRows`
 * locks its block for the same reason (PSTUDIO-2555).
 */
export function numberingNumbersOf(rows: readonly NumberedRow[]): {
  own: number[];
  referenced: number[];
} {
  const own = new Set<number>();
  const referenced = new Set<number>();
  rows.forEach((row) => {
    const num = toValidNodeNumber(row.nodeNumber);
    if (num !== undefined) own.add(num);
    collectReferencedNodeNumbers(row, referenced);
  });
  return { own: [...own], referenced: [...referenced] };
}
